import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { TaskProgressCard, cn, Icon, useOverlayLock, OVERLAY_LOCKED_CLASSES, PanelDock, PANEL_DOCK_SHIFT_PX, panelAuraClassName, panelSurfaceClassName, panelEnterClassName, panelExitClassName, panelStackSlotClassName, panelStackSlotExitClassName, panelStackSlotInnerClassName, panelStaggerStyle, PANEL_EASE, PANEL_DISMISS_MS, PANEL_MOTION_MS } from "@nexus/ui";
import { ListChecks } from "lucide-react";
import type { TaskRecord } from "@nexus/contracts";
import {
  activeTasksQueryOptions,
  notifyPersonaScriptsGenerationCancelled,
  useRetryOpportunitySetTask,
} from "@/lib/queries";
import { cancelTask, dismissTask } from "@/features/task-progress/api/task-progress.api";
import {
  TASK_PROGRESS_PANEL_ID,
  TASK_PROGRESS_TRIGGER_ATTR,
  useTaskSidebarStore,
} from "@/features/task-progress/store/task-sidebar.store";
import { isTaskDone, isTaskFailed, partitionTasks } from "@/features/task-progress/lib/task-counts";
import { assignEnterStaggerWave } from "@/features/task-progress/lib/enter-stagger";
import { resolveTaskNavigation } from "@/features/task-progress/lib/resolve-task-navigation";
import { keepsNavigationParams } from "@/features/task-progress/lib/scripts-tab-task-types";
import { useTaskSidebarHover } from "@/features/task-progress/hooks/useTaskSidebarHover";
import { useStopOpportunityGeneration } from "@/features/task-progress/hooks/useStopOpportunityGeneration";
import { useStopPersonaOpportunityGeneration } from "@/features/task-progress/hooks/useStopPersonaOpportunityGeneration";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { canAnimate } from "@/lib/motion";
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";

/** Only the owner may delete the server-side record; others just hide it locally. */
function isTaskOwner(task: TaskRecord, userEmail: string | null | undefined): boolean {
  return !!userEmail && task.userEmail.toLowerCase() === userEmail.toLowerCase();
}

function TaskSectionClearButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex min-h-11 min-w-11 shrink-0 cursor-pointer items-center justify-center rounded-sm px-1.5 py-0.5",
        "[@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0",
        "text-[10px] font-medium uppercase tracking-wider text-muted-foreground",
        "transition-colors duration-150",
        PANEL_EASE,
        "hover:text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
      )}
    >
      {label}
    </button>
  );
}

function TaskSidebarSection({
  label,
  action,
  children,
}: {
  label?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(true);
  const [resizing, setResizing] = useState(false);
  const resizeTimer = useRef<number | null>(null);
  const contentId = useId();
  const slotGapClassName = "flex flex-col [&_[data-stack-slot]:not(:last-child)_[data-stack-pad]]:pb-2";

  useEffect(
    () => () => {
      if (resizeTimer.current != null) window.clearTimeout(resizeTimer.current);
    },
    [],
  );

  const toggle = () => {
    setOpen((current) => !current);
    if (!canAnimate()) return;
    setResizing(true);
    if (resizeTimer.current != null) window.clearTimeout(resizeTimer.current);
    resizeTimer.current = window.setTimeout(() => setResizing(false), PANEL_MOTION_MS);
  };

  // A group with no heading still needs somewhere to hang its action, so the
  // action row renders on its own when this is the panel's only section.
  if (!label) {
    return (
      <section className={slotGapClassName}>
        {action ? <div className="mb-2 flex justify-end">{action}</div> : null}
        {children}
      </section>
    );
  }

  return (
    <section className="flex flex-col">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={contentId}
            onClick={toggle}
            className={cn(
              "inline-flex min-h-11 min-w-11 w-fit cursor-pointer items-center justify-center gap-1 rounded-sm border border-border bg-muted px-1.5 py-0.5",
              "[@media(hover:hover)_and_(pointer:fine)]:min-h-0 [@media(hover:hover)_and_(pointer:fine)]:min-w-0",
              "text-[10px] font-medium uppercase tracking-wider text-muted-foreground",
              "transition-colors duration-150",
              PANEL_EASE,
              "hover:bg-background hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
            )}
          >
            <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-muted-foreground/50" />
            {label}
            <Icon
              name="chevron-down"
              size="xs"
              color="inherit"
              className={cn(
                "origin-center transition-transform duration-150 motion-reduce:transition-none",
                PANEL_EASE,
                open ? "rotate-0" : "-rotate-90",
              )}
            />
          </button>
        </h3>
        {action}
      </div>
      <div
        id={contentId}
        inert={!open || undefined}
        className={cn(
          "grid",
          "motion-safe:transition-[grid-template-rows] motion-safe:duration-150",
          PANEL_EASE,
          "motion-reduce:transition-none",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        {/* Clip only while the row height is in motion or the section is closed —
            min-h-0 is what lets 0fr actually collapse. An open section that clips
            cuts the bottom edge, shadow and hover lift off its last card.
            Horizontal gutter matches the dock shift so overflow-hidden does not
            slice cards that are translated left during the height animation. */}
        <div
          className={cn("min-w-0", (!open || resizing) && "min-h-0 overflow-hidden")}
          style={{
            paddingLeft: PANEL_DOCK_SHIFT_PX,
            marginLeft: -PANEL_DOCK_SHIFT_PX,
          }}
        >
          <div className={slotGapClassName}>{children}</div>
        </div>
      </div>
    </section>
  );
}

export function TaskProgressSidebar() {
  const { t } = useTranslation();
  const open = useTaskSidebarStore((s) => s.open);
  const collapse = useTaskSidebarStore((s) => s.collapse);
  const { handleHoverOpen, handleHoverClose } = useTaskSidebarHover();
  const dismissedIds = useTaskSidebarStore((s) => s.dismissedIds);
  const dismissLocally = useTaskSidebarStore((s) => s.dismissLocally);
  const restoreLocal = useTaskSidebarStore((s) => s.restoreLocal);
  const retainPresent = useTaskSidebarStore((s) => s.retainPresent);
  const isLocked = useOverlayLock();
  const userEmail = useAuth((s) => s.user?.email);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const setActiveTenant = useAuth((s) => s.setActiveTenant);
  const router = useRouter();

  const { data: allTasks = [] } = useQuery(activeTasksQueryOptions(userEmail ?? undefined));
  const tenants = useAuth((s) => s.tenants);
  const tenantNameById = Object.fromEntries(tenants.map((t) => [t.id, t.name]));

  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());
  const exitTimers = useRef<Map<string, number>>(new Map());
  const enterStaggerById = useRef<Map<string, number>>(new Map());
  const tasks = allTasks.filter((t) => !dismissedIds.has(t.correlationId));

  const hideLocally = useCallback((correlationId: string) => {
    dismissLocally(correlationId);
    setExitingIds((prev) => {
      if (!prev.has(correlationId)) return prev;
      const next = new Set(prev);
      next.delete(correlationId);
      return next;
    });
  }, [dismissLocally]);

  const abortExit = useCallback((correlationId: string) => {
    const existing = exitTimers.current.get(correlationId);
    if (existing != null) {
      window.clearTimeout(existing);
      exitTimers.current.delete(correlationId);
    }
    setExitingIds((prev) => {
      if (!prev.has(correlationId)) return prev;
      const next = new Set(prev);
      next.delete(correlationId);
      return next;
    });
  }, []);

  const playExit = useCallback((correlationId: string, then: () => void) => {
    const existing = exitTimers.current.get(correlationId);
    if (existing != null) window.clearTimeout(existing);
    if (!canAnimate()) {
      then();
      return;
    }
    setExitingIds((prev) => new Set(prev).add(correlationId));
    const timer = window.setTimeout(() => {
      exitTimers.current.delete(correlationId);
      then();
    }, PANEL_DISMISS_MS);
    exitTimers.current.set(correlationId, timer);
  }, []);

  useEffect(() => {
    const timers = exitTimers.current;
    return () => {
      for (const timer of timers.values()) window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!open) enterStaggerById.current.clear();
  }, [open]);

  useEffect(() => {
    if (!open || isLocked) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      const panel = document.getElementById(TASK_PROGRESS_PANEL_ID);
      if (panel?.contains(target)) return;
      const trigger = document.querySelector(`[${TASK_PROGRESS_TRIGGER_ATTR}]`);
      if (trigger?.contains(target)) return;
      if (
        target instanceof Element &&
        target.closest("[role='dialog'], [data-radix-popper-content-wrapper]")
      ) {
        return;
      }
      collapse();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open, isLocked, collapse]);

  const unhide = useCallback(
    (correlationId: string) => {
      restoreLocal(correlationId);
    },
    [restoreLocal],
  );

  const dismissMutation = useMutation({
    mutationFn: dismissTask,
    onError: (_err, correlationId: string) => {
      abortExit(correlationId);
      unhide(correlationId);
    },
  });

  const dismiss = useCallback(
    (correlationId: string, isOwner: boolean) => {
      hideLocally(correlationId);
      if (isOwner) dismissMutation.mutate(correlationId);
    },
    [hideLocally, dismissMutation],
  );

  const prevCountRef = useRef(tasks.length);
  useEffect(() => {
    if (prevCountRef.current > 0 && tasks.length === 0 && open) {
      collapse();
    }
    prevCountRef.current = tasks.length;
  }, [tasks.length, open, collapse]);

  useEffect(() => {
    retainPresent(new Set(allTasks.map((t) => t.correlationId)));
  }, [allTasks, retainPresent]);

  const cancelMutation = useMutation({
    mutationFn: cancelTask,
    onSuccess: (_data, correlationId: string) => {
      playExit(correlationId, () => dismiss(correlationId, true));
    },
    onError: (_err, correlationId: string) => {
      abortExit(correlationId);
      unhide(correlationId);
    },
  });

  const stopOpportunityGeneration = useStopOpportunityGeneration();
  const stopPersonaOpportunityGeneration = useStopPersonaOpportunityGeneration();
  const retryMutation = useRetryOpportunitySetTask();

  const handleCancel = useCallback(
    (task: TaskRecord) => {
      if (task.type === "generate-persona-scripts") {
        notifyPersonaScriptsGenerationCancelled();
      }
      const hideAfterCancelSucceeds = (correlationId: string) => {
        playExit(correlationId, () => hideLocally(correlationId));
      };
      const setId = task.navigation?.params?.setId;
      if (
        setId &&
        task.type === "generate-opportunity-set" &&
        stopOpportunityGeneration(setId, hideAfterCancelSucceeds)
      ) {
        return;
      }
      if (
        setId &&
        task.type === "generate-persona-opportunity-set" &&
        stopPersonaOpportunityGeneration(setId, hideAfterCancelSucceeds)
      ) {
        return;
      }
      cancelMutation.mutate(task.correlationId);
    },
    [hideLocally, playExit, stopOpportunityGeneration, stopPersonaOpportunityGeneration, cancelMutation],
  );

  if (!open) return null;

  const { inProgress, failed, done } = partitionTasks(tasks);
  const sectionCount = [inProgress, failed, done].filter((group) => group.length > 0).length;
  assignEnterStaggerWave(
    enterStaggerById.current,
    [...inProgress, ...failed, ...done].map((task) => task.correlationId),
  );

  const clearGroup = (group: TaskRecord[]) => {
    for (const task of group) {
      playExit(task.correlationId, () =>
        dismiss(task.correlationId, isTaskOwner(task, userEmail)),
      );
    }
  };

  const renderCard = (task: TaskRecord) => {
    const hasNav = !!task.navigation;
    const taskDone = isTaskDone(task) && hasNav;
    const keepParamsWhileActive = keepsNavigationParams(task.type);
    const isOwner = isTaskOwner(task, userEmail);
    const triggeredBy = isOwner
      ? t("taskProgress.startedByYou", "Started by you")
      : t("taskProgress.startedBy", "Started by {{name}}", { name: task.userEmail });
    const exiting = exitingIds.has(task.correlationId);
    const staggerIndex = enterStaggerById.current.get(task.correlationId) ?? 0;

    return (
      <div
        key={task.correlationId}
        data-stack-slot=""
        data-state={exiting ? "exiting" : "entered"}
        className={cn(
          "relative z-10 shrink-0",
          exiting ? panelStackSlotExitClassName : panelStackSlotClassName,
        )}
        style={exiting ? undefined : panelStaggerStyle(staggerIndex)}
      >
        <div data-stack-inner="" className={panelStackSlotInnerClassName}>
          <div
            data-stack-pad=""
            className={exiting ? panelExitClassName : panelEnterClassName}
          >
            <TaskProgressCard
              task={task}
              inline
              tenantName={tenantNameById[task.tenantId]}
              triggeredBy={triggeredBy}
              labels={{
                view: t("taskProgress.view", "View"),
                dismiss: t("taskProgress.dismiss", "Dismiss"),
                retry: t("common.retry", "Retry"),
              }}
              onCancel={isOwner ? () => handleCancel(task) : undefined}
              onDismiss={() => playExit(task.correlationId, () => dismiss(task.correlationId, isOwner))}
              onRetry={
                isTaskFailed(task) && task.type === "generate-opportunity-set"
                  ? () => {
                      if (!retryMutation.isPending) retryMutation.mutate(task);
                    }
                  : undefined
              }
              onNavigate={
                hasNav
                  ? () => {
                      const nav =
                        taskDone || keepParamsWhileActive
                          ? task.navigation!
                          : {
                              ...task.navigation!,
                              params: undefined,
                              itemIds: undefined,
                              section: undefined,
                            };
                      resolveTaskNavigation(nav, router, {
                        activeTenantId,
                        setActiveTenant,
                      });
                    }
                  : undefined
              }
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <aside
      id={TASK_PROGRESS_PANEL_ID}
      inert={isLocked || undefined}
      aria-busy={isLocked || undefined}
      onMouseEnter={handleHoverOpen}
      onMouseLeave={handleHoverClose}
      className={cn(
        // Hug the card stack; 6rem = top-20 + bottom-4 so a long list still
        // clears the header and the viewport edge.
        "fixed right-0 top-20 z-50 flex max-h-[calc(100vh-6rem)] w-[24.5rem] max-w-[calc(100vw-1rem)] flex-col",
        isLocked && OVERLAY_LOCKED_CLASSES,
      )}
      aria-label={t("taskProgress.activeTasks", "Active tasks")}
    >
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div aria-hidden className={panelAuraClassName} />
        <div
          data-slot="task-progress-scroll"
          className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain py-2 pl-6 pr-3"
        >
          <PanelDock className="relative isolate w-full pb-6">
            <div className="relative flex flex-col gap-3">
              {tasks.length === 0 ? (
                <div className={cn("flex w-fit items-center justify-center gap-2 self-center px-4 py-3 text-muted-foreground", panelSurfaceClassName, panelEnterClassName)}>
                  <ListChecks className="h-4 w-4 opacity-60" />
                  <p className="text-sm">{t("taskProgress.noActiveTasks", "No active tasks")}</p>
                </div>
              ) : (
                <>
                  {inProgress.length > 0 ? (
                    <TaskSidebarSection label={sectionCount > 1 ? t("taskProgress.inProgress", "In progress") : undefined}>
                      {inProgress.map(renderCard)}
                    </TaskSidebarSection>
                  ) : null}
                  {failed.length > 0 ? (
                    <TaskSidebarSection label={sectionCount > 1 ? t("taskProgress.failed", "Failed") : undefined}>
                      {failed.map(renderCard)}
                    </TaskSidebarSection>
                  ) : null}
                  {done.length > 0 ? (
                    <TaskSidebarSection
                      label={
                        sectionCount > 1
                          ? t("taskProgress.completeSection", "Completed ({{count}})", { count: done.length })
                          : undefined
                      }
                      action={
                        <TaskSectionClearButton
                          label={t("taskProgress.clearCompleted", "Clear completed")}
                          onClick={() => clearGroup(done)}
                        />
                      }
                    >
                      {done.map(renderCard)}
                    </TaskSidebarSection>
                  ) : null}
                </>
              )}
            </div>
          </PanelDock>
        </div>
      </div>
    </aside>
  );
}
